import { STRINGS } from '@/constants/strings';

import type {
  GuardianNotification,
  GuardianNotificationApiCategory,
  GuardianNotificationApiItem,
  GuardianNotificationFilter,
  GuardianNotificationType,
  VisibleGuardianNotificationApiItem,
} from './types';

const strings = STRINGS.guardian.notifications;

export const NOTIFICATION_FILTERS: ReadonlyArray<{
  value: GuardianNotificationFilter;
  label: string;
}> = [
  { value: 'all', label: strings.filters.all },
  { value: 'class-reminders', label: strings.filters.classReminders },
  { value: 'pins-notes', label: strings.filters.pinsAndCoachNotes },
  { value: 'tournament-updates', label: strings.filters.tournamentUpdates },
  { value: 'co-guardian-activities', label: strings.filters.coGuardianActivities },
];

export const NOTIFICATION_TYPE_LABELS: Record<GuardianNotificationType, string> = {
  'class-reminders': strings.filters.classReminders,
  'pins-notes': strings.filters.pinsAndCoachNotes,
  'tournament-updates': strings.filters.tournamentUpdates,
  'co-guardian-activities': strings.filters.coGuardianActivities,
};

export const NOTIFICATION_CATEGORY_TO_TYPE: Record<
  Exclude<GuardianNotificationApiCategory, 'shopDeals'>,
  GuardianNotificationType
> = {
  classReminders: 'class-reminders',
  coachNotes: 'pins-notes',
  tournamentUpdates: 'tournament-updates',
  coGuardianActivity: 'co-guardian-activities',
};

export const NOTIFICATION_TYPE_ICONS = {
  'class-reminders': 'IcNotificationClass',
  'pins-notes': 'IcNotificationNote',
  'tournament-updates': 'IcNotificationAward',
  'co-guardian-activities': 'IcNotificationPeople',
} as const;

export function isVisibleGuardianNotification(
  item: GuardianNotificationApiItem,
): item is VisibleGuardianNotificationApiItem {
  return item.category !== 'shopDeals';
}

function formatNotificationTime(createdAt: string): string {
  const elapsed = Math.max(0, Date.now() - new Date(createdAt).getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return strings.time.now;
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function mapGuardianNotification(
  item: VisibleGuardianNotificationApiItem,
): GuardianNotification {
  const type = NOTIFICATION_CATEGORY_TO_TYPE[item.category];
  return {
    id: item.id,
    type,
    title: item.title,
    subtitle: item.subtitle ?? '',
    receivedAt: new Date(item.createdAt).getTime(),
    timeLabel: formatNotificationTime(item.createdAt),
    isRead: item.read,
    icon: NOTIFICATION_TYPE_ICONS[type],
    destination: item.destination,
    eventId: item.eventId ?? null,
    playerId: item.playerId,
    sessionId: item.sessionId,
    bookingId: item.bookingId,
    invitationToken: item.invitationToken,
    apiType: item.type,
  };
}
